import React, {
  lazy,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from "react";
import { Container, Row } from "reactstrap";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import NavBar from "../../components/NavBar";
import AgendaUpcomingSidebar from "../../components/agenda/AgendaUpcomingSidebar";
import AgendaHeroBanner from "../../components/agenda/AgendaHeroBanner";
import AgendaControls from "../../components/agenda/AgendaControls";
import AgendaExploreGridView from "../../components/agenda/AgendaExploreGridView";
import useAgendaEvents from "../../hooks/useAgendaEvents";
import { useCustomToast } from "../../components/common/CustomToast";
import {
  filterEventsByCategory,
  getAgendaEventApiId,
  groupEventsForSidebar,
  mapEventDetailForAgendaEditor,
  resolveAgendaImageUrl,
  releaseAgendaScrollLock,
} from "../../utils/agendaUtils";
import { getAgendaEventDetail } from "../../services/agendaApi";
import AgendaCalendarView from "../../components/agenda/AgendaCalendarView";
import "../../components/agenda/AgendaFoundation.css";
import { Language } from "../../App";

import CreateAgendaEventModal from "../../components/agenda/CreateAgendaEventModal";
import MentorShipSchedule from "../../components/Mentorship/MentorShipSchedule";
import moment from "moment";
import { CHAT } from "../../constants/RoutesUrl";
import { apiAdmin } from "../../services/api";
import { useSplashAds } from "../../hooks/useSplashAds";
import placeholderAvatar from "../../assets/images/icon/placeholder_Awatar.png";

const AgendaEventModal = lazy(() =>
  import("../../components/agenda/AgendaEventModal")
);
const AgendaInviteDrawer = lazy(() =>
  import("../../components/agenda/AgendaInviteDrawer")
);

const CREATE_DRAWER_EXIT_DURATION = 230;

function getExplorePerPage() {
  if (typeof window === "undefined") return 16;
  const width = window.innerWidth;
  if (width >= 2600) return 24; // 6 columns x 4 rows = 24
  if (width >= 2000) return 20; // 5 columns x 4 rows = 20
  if (width >= 1600) return 16; // 4 columns x 4 rows = 16
  if (width > 1080) return 15;  // 3 columns x 5 rows = 15
  if (width > 640) return 12;   // 2 columns x 6 rows = 12
  return 10;                    // 1 column x 10 rows = 10
}

const Agenda = () => {
  const { lang } = useContext(Language) || {};
  const { showToast } = useCustomToast();
  const { ads: splashAds, isReady: areSplashAdsReady } = useSplashAds();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Helper to resolve initial category & view from URL or location state
  const resolvedCategory = useMemo(() => {
    const raw =
      searchParams.get("category") ||
      searchParams.get("filter") ||
      searchParams.get("type") ||
      location.state?.category ||
      location.state?.filter ||
      location.state?.type;

    if (!raw) return "all";
    const lower = String(raw).toLowerCase().trim();
    if (lower === "event" || lower === "events") return "event";
    if (lower === "webinar" || lower === "webinars") return "webinar";
    if (
      lower === "clinical_session" ||
      lower === "clinical-session" ||
      lower === "clinical-sessions" ||
      lower === "session" ||
      lower === "sessions" ||
      lower === "clinical"
    ) {
      return "clinical_session";
    }
    if (lower === "mentorship" || lower === "mentorships" || lower === "mentor") {
      return "mentorship";
    }
    return lower;
  }, [searchParams, location.state]);

  const resolvedViewMode = useMemo(() => {
    const rawView =
      searchParams.get("view") ||
      searchParams.get("tab") ||
      searchParams.get("mode") ||
      location.state?.viewMode ||
      location.state?.view ||
      location.state?.tab;

    if (rawView === "schedules" || rawView === "calendar") return "schedules";
    if (rawView === "saved" || rawView === "completed") return rawView;
    if (rawView === "explore" || rawView === "grid") return "explore";
    if (resolvedCategory === "clinical_session" || resolvedCategory === "mentorship") {
      return "schedules";
    }
    return "explore";
  }, [searchParams, location.state, resolvedCategory]);

  const [viewMode, setViewMode] = useState(resolvedViewMode); // "explore" or "schedules"
  const [scheduleCategory, setScheduleCategory] = useState(resolvedCategory);
  const [exploreCategory, setExploreCategory] = useState(
    resolvedCategory === "clinical_session" || resolvedCategory === "mentorship" ? "all" : resolvedCategory
  );
  const [explorePerPage, setExplorePerPage] = useState(getExplorePerPage);

  // Sync state when URL params or location.state change
  useEffect(() => {
    setViewMode(resolvedViewMode);
    setScheduleCategory(resolvedCategory);
    setExploreCategory(
      resolvedCategory === "clinical_session" || resolvedCategory === "mentorship"
        ? "all"
        : resolvedCategory
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [resolvedViewMode, resolvedCategory]);

  useEffect(() => {
    const handleResize = () => {
      const next = getExplorePerPage();
      setExplorePerPage((prev) => (prev !== next ? next : prev));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const agendaCollection =
    viewMode === "saved" || viewMode === "completed" ? viewMode : "calendar";

  // Map the active explore filter category to an entity_type string for the API.
  // "all" = fetch both events and webinars (or all 4 types for completed); specific filter = fetch only that type.
  const exploreEntityType = useMemo(() => {
    if (!exploreCategory || exploreCategory === "all") {
      if (viewMode === "completed") {
        return "event,webinar,clinical-session,mentor_request";
      }
      return "event,webinar";
    }
    return exploreCategory; // "event", "webinar", "clinical_session", "mentorship", etc.
  }, [exploreCategory, viewMode]);

  // Calendar API Call with Infinite Query Pagination for Explore list
  const {
    listEvents,
    upcomingEvents,
    suggestionEvents,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasMore,
    loadMore,
    prependEvent,
    refreshEvents,
    replaceEvent,
    removeEvent,
  } = useAgendaEvents({
    entityType: exploreEntityType,
    perPage: explorePerPage,
    collection: agendaCollection,
  });

  // Keep the sidebar tied to the regular calendar data. Saved and Completed
  // only affect the main content area.
  const {
    upcomingEvents: sidebarUpcomingEvents,
    suggestionEvents: sidebarSuggestionEvents,
    isLoading: isSidebarLoading,
    isFetching: isSidebarFetching,
  } = useAgendaEvents({
    entityType: "event,webinar",
    perPage: explorePerPage,
    collection: "calendar",
  });

  // Explore grid view: server already filters by entityType, no client-side filter needed.
  const filteredExploreEvents = listEvents;

  // Schedules calendar view: local filtering on upcoming array
  const filteredScheduleEvents = useMemo(() => {
    return filterEventsByCategory(upcomingEvents, scheduleCategory);
  }, [upcomingEvents, scheduleCategory]);

  // Advertisements from API response
  const agendaAds = useMemo(() => {
    return splashAds?.agenda_center || [];
  }, [splashAds]);

  const sidebarAds = useMemo(() => {
    return splashAds?.agenda_left || [];
  }, [splashAds]);

  const isAdsLoading = !areSplashAdsReady;

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [isCreateDrawerMounted, setIsCreateDrawerMounted] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [isEventDetailLoading, setIsEventDetailLoading] = useState(false);
  const [inviteDrawerOpen, setInviteDrawerOpen] = useState(false);
  const [isInviteDrawerMounted, setIsInviteDrawerMounted] = useState(false);
  const [inviteDrawerData, setInviteDrawerData] = useState({
    itemId: null,
    type: "webinar",
  });
  const [mentorshipScheduleModalOpen, setMentorshipScheduleModalOpen] = useState(false);
  const [selectedMentorshipSession, setSelectedMentorshipSession] = useState(null);
  const createDrawerCloseTimer = useRef(null);
  const createDrawerScrollReleaseFrame = useRef(null);
  const shouldReleaseCreateDrawerScroll = useRef(false);
  const eventDetailRequestRef = useRef(0);
  // The Schedules calendar renders below the hero banner + controls, so on a
  // tall/scrolled-down page it can load off-screen. Scrolled into view below.
  const viewContentRef = useRef(null);

  useEffect(
    () => () => {
      if (createDrawerCloseTimer.current) {
        window.clearTimeout(createDrawerCloseTimer.current);
      }
      if (createDrawerScrollReleaseFrame.current) {
        window.cancelAnimationFrame(createDrawerScrollReleaseFrame.current);
      }
      eventDetailRequestRef.current += 1;
    },
    []
  );

  // Ensure scrolling is always cleanly unlocked on page mount, unmount, and route navigation
  useEffect(() => {
    releaseAgendaScrollLock(true);
    return () => {
      releaseAgendaScrollLock(true);
    };
  }, []);

  // When all drawers and modals are closed, purge any stale scroll locks from DOM
  useEffect(() => {
    if (
      !createModalOpen &&
      !modalOpen &&
      !inviteDrawerOpen &&
      !mentorshipScheduleModalOpen &&
      !isCreateDrawerMounted &&
      !isInviteDrawerMounted
    ) {
      releaseAgendaScrollLock(true);
    }
  }, [
    createModalOpen,
    modalOpen,
    inviteDrawerOpen,
    mentorshipScheduleModalOpen,
    isCreateDrawerMounted,
    isInviteDrawerMounted,
  ]);

  // Bring the Schedules calendar smoothly into view when switching to it —
  // it renders below the hero banner + controls, so from a scrolled-down
  // sidebar position it could otherwise appear "off-screen" until the user
  // scrolls manually. The offset clears NavBar's fixed floating island
  // (~5rem, same gutter .agenda-wrapper already reserves for it) so the
  // calendar doesn't land tucked underneath it.
  useEffect(() => {
    if (viewMode !== "schedules" || !viewContentRef.current) return;
    const target = viewContentRef.current;
    const scrollTop =
      window.scrollY + target.getBoundingClientRect().top - 90;
    window.scrollTo({ top: Math.max(scrollTop, 0), behavior: "smooth" });
  }, [viewMode]);

  const hasUpcoming = Boolean(
    sidebarUpcomingEvents && sidebarUpcomingEvents.length > 0
  );
  const hasSuggestions = Boolean(
    sidebarSuggestionEvents && sidebarSuggestionEvents.length > 0
  );
  const isSuggestionsActive = !hasUpcoming && hasSuggestions;

  // Grouped schedules for the left sidebar:
  // Uses direct upcoming array (or suggestions if empty).
  // Upcoming filter is not applied and remains static across category switches.
  const groupedSchedules = useMemo(() => {
    const isDirectUpcoming = Boolean(
      sidebarUpcomingEvents && sidebarUpcomingEvents.length > 0
    );
    const isDirectSuggestions =
      !isDirectUpcoming &&
      Boolean(sidebarSuggestionEvents && sidebarSuggestionEvents.length > 0);
    const isDirect = isDirectUpcoming || isDirectSuggestions;

    let source = sidebarUpcomingEvents;
    if (!isDirectUpcoming && isDirectSuggestions) {
      source = sidebarSuggestionEvents;
    }

    return groupEventsForSidebar(source, "all", isDirect, lang);
  }, [sidebarUpcomingEvents, sidebarSuggestionEvents, lang]);

  const knownEvents = useMemo(() => {
    const eventsById = new Map();
    [
      ...listEvents,
      ...upcomingEvents,
      ...suggestionEvents,
      ...sidebarUpcomingEvents,
      ...sidebarSuggestionEvents,
    ].forEach((event) => {
      if (event && !eventsById.has(event.id)) {
        eventsById.set(event.id, event);
      }
    });

    return Array.from(eventsById.values());
  }, [
    listEvents,
    upcomingEvents,
    suggestionEvents,
    sidebarUpcomingEvents,
    sidebarSuggestionEvents,
  ]);

  const navigate = useNavigate();

  const handleSelectEvent = (event) => {
    if (!event) return;
    const cleanId = event.id
      ? String(event.id).replace(/^(webinar-|session-|evt-)/, "")
      : "";

    if (
      event.type === "webinar" ||
      event.category === "webinar" ||
      event.entity_type === "webinar"
    ) {
      navigate(cleanId ? `/webinar-details/${cleanId}` : "/webinar-details");
      return;
    }

    if (
      event.type === "clinical_session" ||
      event.category === "clinical_session" ||
      event.entity_type === "clinical_session"
    ) {
      navigate(cleanId ? `/session-details/${cleanId}` : "/session-details");
      return;
    }

    if (
      event.type === "mentorship" ||
      event.category === "mentorship" ||
      event.entity_type === "mentorship" ||
      event.entity_type === "mentor_request" ||
      event.entity_type === "mentor-request" ||
      String(event.id || "").startsWith("mentorship-")
    ) {
      const currentUserId =
        JSON.parse(localStorage.getItem("SignupData") || "null")?.data?.id ||
        JSON.parse(localStorage.getItem("SplashData") || "null")?.user?.id;
      const currentId = currentUserId ? Number(currentUserId) : null;

      const raw = event.raw || event;
      let rawMeta = raw.meta || event.meta || {};
      if (typeof rawMeta === "string") {
        try {
          rawMeta = JSON.parse(rawMeta);
        } catch (e) {
          rawMeta = {};
        }
      }

      const mentorObj = raw.mentor || event.mentor || {};
      const menteeObj = raw.user || event.user || {};

      const mentorId = Number(
        mentorObj.id || rawMeta?.mentor_id || raw.mentor_id || 0
      );
      const menteeId = Number(
        menteeObj.id || raw.user_id || event.user_id || 0
      );

      // Check if logged-in user is the MENTOR:
      // If currentId === mentorId -> current user is MENTOR
      // Otherwise -> current user is MENTEE
      const isMentorUser = Boolean(
        currentId && mentorId && currentId === mentorId
      );

      if (isMentorUser) {
        // Mentor: Open the MentorShipSchedule popup modal for the mentee
        const cleanMentorshipId = String(
          raw.id || rawMeta.request_id || event.apiId || event.raw_id || event.id || ""
        ).replace(/^(?:mentorship-|mentor-|session-|evt-)/i, "");

        const menteeName =
          menteeObj.name ||
          [menteeObj.first_name, menteeObj.last_name].filter(Boolean).join(" ") ||
          "Mentee";

        const menteeAvatar = resolveAgendaImageUrl(
          menteeObj.image,
          placeholderAvatar
        );

        // Resolve Start Timestamp / Date / Time
        let startTimestamp = raw.start_timestamp ? Number(raw.start_timestamp) : null;
        let startDate = raw.start_date || raw.date || event.date || "";
        let startTime = raw.start_time || event.startTime || event.time || "";

        if (!startTimestamp && event.startDateObj instanceof Date && !isNaN(event.startDateObj.getTime())) {
          startTimestamp = Math.floor(event.startDateObj.getTime() / 1000);
          if (!startDate) startDate = moment(event.startDateObj).format("YYYY-MM-DD");
          if (!startTime) startTime = moment(event.startDateObj).format("HH:mm:ss");
        } else if (startTimestamp) {
          const m = moment.unix(startTimestamp);
          if (m.isValid()) {
            if (!startDate) startDate = m.format("YYYY-MM-DD");
            if (!startTime) startTime = m.format("HH:mm:ss");
          }
        }

        // Resolve Duration (minutes)
        let durationMinutes = raw.duration || event.duration;
        if (!durationMinutes && raw.start_timestamp && raw.end_timestamp) {
          const diffSec = Number(raw.end_timestamp) - Number(raw.start_timestamp);
          if (diffSec > 0) durationMinutes = Math.round(diffSec / 60);
        }
        if (!durationMinutes && event.startDateObj instanceof Date && event.endDateObj instanceof Date) {
          const diffMs = event.endDateObj.getTime() - event.startDateObj.getTime();
          if (diffMs > 0) durationMinutes = Math.round(diffMs / 60000);
        }
        if (!durationMinutes) durationMinutes = 15;

        // Resolve Timezone
        const timezone =
          rawMeta.timezone ||
          raw.timezone ||
          event.timezone ||
          sessionStorage.getItem("user_timezone") ||
          "Asia/Karachi";

        // Resolve Meeting URL
        const meetingUrl =
          raw.meeting_url ||
          raw.meeting_host_url ||
          raw.link ||
          rawMeta.meeting_url ||
          rawMeta.link ||
          event.meetingUrl ||
          event.link ||
          "";

        // Status: Calendar / upcoming sessions are confirmed bookings
        const isLive =
          raw.status === 2 ||
          raw.status === "2" ||
          raw.status === "live" ||
          raw.status === "started" ||
          event.status === "live" ||
          event.status === "started";

        const resolvedStatus = isLive ? "Mentorship in Progress" : "Sessions Confirmed";

        const cvUrl = raw.data ? resolveAgendaImageUrl(raw.data, null) : null;

        const menteeModalData = {
          id: Number(cleanMentorshipId) || Number(event.id) || 0,
          name: menteeName,
          image: menteeAvatar,
          profileId: menteeId || null,
          user_title: menteeObj.user_title || menteeObj.sub_type || "",
          cvUrl: cvUrl,
          cvFileName: raw.data ? raw.data.split("/").pop() : "",
          startDate,
          startTime,
          startTimestamp: startTimestamp ? String(startTimestamp) : "",
          durationMinutes: String(durationMinutes),
          timezone,
          meetingUrl,
          status: resolvedStatus,
        };

        setSelectedMentorshipSession(menteeModalData);
        setMentorshipScheduleModalOpen(true);

        // Fetch full activity details asynchronously to hydrate real mentee details
        if (cleanMentorshipId) {
          apiAdmin
            .post("user/mentor/activity", { type: "mentor", status: "active" })
            .then((res) => {
              const list = res?.data?.data?.data || [];
              let item = list.find((a) => String(a.id) === String(cleanMentorshipId));
              if (!item) {
                return apiAdmin
                  .post("user/mentor/activity", { type: "mentor", status: "completed" })
                  .then((res2) => {
                    const list2 = res2?.data?.data?.data || [];
                    return list2.find((a) => String(a.id) === String(cleanMentorshipId));
                  });
              }
              return item;
            })
            .then((found) => {
              if (found) {
                const u = found.user || {};
                const uName = u.name || [u.first_name, u.last_name].filter(Boolean).join(" ") || menteeName;
                const uAvatar = resolveAgendaImageUrl(u.image, menteeAvatar);
                const cv = found.data ? resolveAgendaImageUrl(found.data, null) : null;

                setSelectedMentorshipSession((prev) => {
                  if (!prev || String(prev.id) !== String(cleanMentorshipId)) return prev;
                  return {
                    ...prev,
                    name: uName,
                    image: uAvatar,
                    profileId: u.id || prev.profileId,
                    user_title: u.user_title || u.sub_type || prev.user_title,
                    cvUrl: cv || prev.cvUrl,
                    cvFileName: found.data ? found.data.split("/").pop() : prev.cvFileName,
                    startDate: found.start_date || prev.startDate,
                    startTime: found.start_time || prev.startTime,
                    startTimestamp: found.start_timestamp ? String(found.start_timestamp) : prev.startTimestamp,
                    durationMinutes: found.duration ? String(found.duration) : prev.durationMinutes,
                    timezone: found.timezone || prev.timezone,
                    meetingUrl: found.meeting_url || found.meeting_host_url || prev.meetingUrl,
                  };
                });
              }
            })
            .catch((err) => {
              console.warn("Could not fetch mentorship activity detail:", err);
            });
        }
        return;
      } else {
        // Mentee: Navigate to chat with the mentor to view details & join session link
        const targetMentorId = mentorId || 410;
        const targetMentorName =
          mentorObj.name ||
          [mentorObj.first_name, mentorObj.last_name].filter(Boolean).join(" ") ||
          "Doctor";
        const targetMentorImage = resolveAgendaImageUrl(
          mentorObj.image,
          placeholderAvatar
        );
        const targetMentorTitle =
          mentorObj.user_title || mentorObj.sub_type || "Doctor";

        navigate(CHAT, {
          state: {
            from: currentId,
            to: targetMentorId,
            user_id: targetMentorId,
            group_id: 0,
            user: {
              id: targetMentorId,
              user_id: targetMentorId,
              name: targetMentorName,
              image: targetMentorImage,
              user_title: targetMentorTitle,
            },
            selectedUser: {
              id: targetMentorId,
              name: targetMentorName,
              image: targetMentorImage,
              user_title: targetMentorTitle,
            },
          },
        });
        return;
      }
    }

    if (
      event.type === "event" ||
      event.category === "event" ||
      event.entity_type === "event"
    ) {
      navigate(cleanId ? `/event-details/${cleanId}` : "/event-details");
      return;
    }

    setSelectedEvent(event);
    setModalOpen(true);
  };

  const clearCreateDrawerCloseTimer = useCallback(() => {
    if (createDrawerCloseTimer.current) {
      window.clearTimeout(createDrawerCloseTimer.current);
      createDrawerCloseTimer.current = null;
    }
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    eventDetailRequestRef.current += 1;
    setIsEventDetailLoading(false);
    setCreateModalOpen(false);
    clearCreateDrawerCloseTimer();
    createDrawerCloseTimer.current = window.setTimeout(() => {
      setIsCreateDrawerMounted(false);
      setEditingEvent(null);
      createDrawerCloseTimer.current = null;
    }, CREATE_DRAWER_EXIT_DURATION);
  }, [clearCreateDrawerCloseTimer]);

  const openCreateDrawer = useCallback(
    (event = null) => {
      clearCreateDrawerCloseTimer();
      setEditingEvent(event);
      setIsCreateDrawerMounted(true);
      setCreateModalOpen(true);
    },
    [clearCreateDrawerCloseTimer]
  );

  const handleOpenCreateModal = useCallback(() => {
    eventDetailRequestRef.current += 1;
    setIsEventDetailLoading(false);
    openCreateDrawer();
  }, [openCreateDrawer]);

  const handleEditEvent = useCallback(async (event) => {
    if (!event || event.type === "mentorship") return;

    // If it's a recurring instance, find original base event
    const targetEvent = event.originalId
      ? knownEvents.find((e) => e.id === event.originalId) || event
      : event;
    const requestId = eventDetailRequestRef.current + 1;
    eventDetailRequestRef.current = requestId;

    // Calendar data is intentionally compact. Event edits need the dedicated
    // detail endpoint so optional fields are not lost on save.
    if (targetEvent.type === "event") {
      const eventId = getAgendaEventApiId(targetEvent);
      if (!eventId) {
        setIsEventDetailLoading(false);
        showToast(
          lang?.unable_load_event_details || "Unable to load the event details. Please try again.",
          "error"
        );
        return;
      }

      // Open the drawer immediately, then keep its form unavailable until the
      // complete edit record has replaced the compact calendar item.
      setIsEventDetailLoading(true);
      openCreateDrawer(targetEvent);

      try {
        const eventDetail = await getAgendaEventDetail(eventId, targetEvent.type || "event");

        // Ignore an earlier response when the user selects another event first.
        if (requestId !== eventDetailRequestRef.current) return;

        setEditingEvent(mapEventDetailForAgendaEditor(eventDetail, targetEvent));
      } catch (error) {
        if (requestId !== eventDetailRequestRef.current) return;

        showToast(
          error?.response?.data?.action ||
          error?.response?.data?.message ||
          error?.message ||
          (lang?.unable_load_event_details || "Unable to load the event details. Please try again."),
          "error"
        );
        handleCloseCreateModal();
      } finally {
        if (requestId === eventDetailRequestRef.current) {
          setIsEventDetailLoading(false);
        }
      }

      return;
    }

    setIsEventDetailLoading(false);
    openCreateDrawer(targetEvent);
  }, [
    handleCloseCreateModal,
    knownEvents,
    openCreateDrawer,
    showToast,
  ]);

  const handleAddEvent = useCallback(
    (newEvent) => {
      if (!newEvent) return;
      prependEvent(newEvent);
      refreshEvents();
    },
    [prependEvent, refreshEvents]
  );

  const handleUpdateEvent = useCallback(
    (updatedEvent) => {
      if (!updatedEvent) return;

      replaceEvent(updatedEvent);

      if (selectedEvent && selectedEvent.id === updatedEvent.id) {
        setSelectedEvent(updatedEvent);
      }

      refreshEvents();
    },
    [refreshEvents, replaceEvent, selectedEvent]
  );

  const handleFavoriteToggle = useCallback(
    (item, isFav) => {
      if (!item) return;
      if (viewMode === "saved" && !isFav) {
        removeEvent(item.id);
      } else {
        replaceEvent({
          ...item,
          isSaved: isFav,
          isFavorited: isFav,
          is_favorite: isFav,
        });
      }
    },
    [viewMode, removeEvent, replaceEvent]
  );

  const handleOpenInviteDrawer = useCallback(({ itemId, type }) => {
    if (!itemId) return;
    // Invites drawer is currently only for webinars and clinical sessions
    if (type === "event") return;

    setInviteDrawerData({ itemId, type });
    setIsInviteDrawerMounted(true);
    setInviteDrawerOpen(true);
  }, []);

  const handleCloseInviteDrawer = useCallback(() => {
    setInviteDrawerOpen(false);
  }, []);

  // Reset the target view filter to default ("all") on tab shift and fetch data
  const handleTabChange = useCallback((newMode) => {
    setViewMode(newMode);
    if (newMode === "schedules") {
      setScheduleCategory("all");
    } else {
      setExploreCategory("all");
    }
  }, []);

  // Wrapper for explore category changes
  const handleSetExploreCategory = useCallback((cat) => {
    setExploreCategory(cat);
  }, []);

  const isScheduleView = viewMode === "schedules";
  const currentCategory = isScheduleView ? scheduleCategory : exploreCategory;
  const setCurrentCategory = isScheduleView ? setScheduleCategory : handleSetExploreCategory;

  return (
    <Container fluid className="agenda-page px-0">
      {/* Top Floating Island Navigation Bar */}
      <Row className="m-0">
        <NavBar />
      </Row>

      {/* Main 2-Column Responsive Layout */}
      <div className="agenda-main-layout feed-aligned-page-layout">
        {/* Left Column: Upcoming Schedules & Promo */}
        <AgendaUpcomingSidebar
          groupedSchedules={groupedSchedules}
          onSelectEvent={handleSelectEvent}
          onCreateEvent={handleOpenCreateModal}
          isLoading={
            isSidebarLoading ||
            (isSidebarFetching &&
              !sidebarUpcomingEvents.length &&
              !sidebarSuggestionEvents.length)
          }
          selectedCategory={scheduleCategory}
          ads={sidebarAds}
          isAdsLoading={isAdsLoading}
          isSuggested={isSuggestionsActive}
        />

        {/* Right Column: Hero Banner + Controls + Views */}
        <main className="agenda-content-area position-relative">
          {/* Top Hero Banner */}
          <AgendaHeroBanner ads={agendaAds} loading={isAdsLoading} />

          {/* Action & Filter Controls */}
          <AgendaControls
            viewMode={viewMode}
            setViewMode={handleTabChange}
            selectedCategory={currentCategory}
            setSelectedCategory={setCurrentCategory}
            onCreateClick={handleOpenCreateModal}
          />

          {/* Main View Area: Calendar or Grid */}
          <div className="agenda-view-content-wrapper" ref={viewContentRef}>
            {viewMode === "schedules" ? (
              <div key="view-schedules" className="agenda-tab-view-fade-in">
                <AgendaCalendarView
                  events={filteredScheduleEvents}
                  onSelectEvent={handleSelectEvent}
                  selectedCategory={scheduleCategory}
                />
              </div>
            ) : (
              <div key={`view-${viewMode}`} className="agenda-tab-view-fade-in">
                <AgendaExploreGridView
                  events={filteredExploreEvents}
                  onSelectEvent={handleSelectEvent}
                  onEditEvent={handleEditEvent}
                  onFavorite={handleFavoriteToggle}
                  selectedCategory={exploreCategory}
                  isLoading={isLoading}
                  isFetching={isFetching}
                  isFetchingNextPage={isFetchingNextPage}
                  hasMore={hasMore}
                  loadMore={loadMore}
                />
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Event Details Preview Modal */}
      {modalOpen && selectedEvent && (
        <Suspense fallback={null}>
          <AgendaEventModal
            isOpen={modalOpen}
            toggle={() => setModalOpen((prev) => !prev)}
            event={selectedEvent}
            onEdit={handleEditEvent}
          />
        </Suspense>
      )}

      {/* Create / Edit Event / Session Modal */}
      {isCreateDrawerMounted && (
        <Suspense fallback={null}>
          <CreateAgendaEventModal
            isOpen={createModalOpen}
            toggle={handleCloseCreateModal}
            onAddEvent={handleAddEvent}
            editEvent={editingEvent}
            isLoadingDetails={isEventDetailLoading}
            onUpdateEvent={handleUpdateEvent}
            onInviteSchedule={handleOpenInviteDrawer}
          />
        </Suspense>
      )}

      {isInviteDrawerMounted && (
        <Suspense fallback={null}>
          <AgendaInviteDrawer
            isOpen={inviteDrawerOpen}
            toggle={handleCloseInviteDrawer}
            itemId={inviteDrawerData.itemId}
            type={inviteDrawerData.type}
          />
        </Suspense>
      )}

      {/* Mentorship Schedule / Reschedule Modal for Mentor */}
      <MentorShipSchedule
        isOpen={mentorshipScheduleModalOpen}
        toggle={() => setMentorshipScheduleModalOpen(false)}
        mentee={selectedMentorshipSession}
        onScheduled={() => {
          setMentorshipScheduleModalOpen(false);
          refreshEvents();
        }}
      />
    </Container>
  );
};

export default memo(Agenda);
